"use client";

import { Capacitor } from "@capacitor/core";
import { createClientLogger } from "@/lib/logging/client";
import { HiiveLocationPermissions } from "./hiive-location-permissions";

const logger = createClientLogger("lib/native/permission-settings");

export type PermissionPlatform = "web" | "ios" | "android";

export type PermissionSettingsTarget =
  | "app"
  | "camera"
  | "contacts"
  | "location"
  | "microphone"
  | "notifications";

export function getPermissionPlatform(): PermissionPlatform {
  if (!Capacitor.isNativePlatform()) return "web";
  const platform = Capacitor.getPlatform();
  return platform === "ios" || platform === "android" ? platform : "web";
}

export function canOpenPermissionSettings(
  _target: PermissionSettingsTarget,
): boolean {
  return getPermissionPlatform() !== "web";
}

async function openAppSettings() {
  await HiiveLocationPermissions.openAppSettings();
}

async function openLocationSettings() {
  try {
    const { BackgroundGeolocation } = await import(
      "@capgo/background-geolocation"
    );
    if (typeof BackgroundGeolocation.openSettings === "function") {
      await BackgroundGeolocation.openSettings();
      return;
    }
  } catch (error) {
    logger.warn("Failed to open native location settings:", error);
  }

  await openAppSettings();
}

async function openContactsSettings() {
  try {
    const { CapacitorContacts } = await import("@capgo/capacitor-contacts");
    if (typeof CapacitorContacts.openSettings === "function") {
      await CapacitorContacts.openSettings();
      return;
    }
  } catch (error) {
    logger.warn("Failed to open native contacts settings:", error);
  }

  await openAppSettings();
}

export async function openPermissionSettings(
  target: PermissionSettingsTarget,
): Promise<boolean> {
  if (getPermissionPlatform() === "web") return false;

  try {
    if (target === "notifications" && getPermissionPlatform() === "android") {
      try {
        await HiiveLocationPermissions.openNotificationSettings();
      } catch {
        // Older binaries expose only app settings.
        await openAppSettings();
      }
      return true;
    }
    if (target === "location") {
      await openLocationSettings();
      return true;
    }

    if (target === "contacts") {
      await openContactsSettings();
      return true;
    }

    await openAppSettings();
    return true;
  } catch (error) {
    logger.warn("Failed to open native app settings:", error);
    return false;
  }
}
